import { useProtocolPreview } from '@services/contracts/mutations/useProtocolPreview'
import { useDeleteProtocol } from '@services/flats/mutations/useDeleteProtocol'
import { useUploadProtocol } from '@services/flats/mutations/useUploadProtocol'
import { useDownloadProtocol } from '@services/flats/queries/useDownloadProtocol'
import { useFlatProtocols } from '@services/flats/queries/useFlatProtocols'
import { Eye, FileCheck, FileText, Sparkles, Trash2, Upload } from 'lucide-react'
import { useRef } from 'react'
import { useTranslation } from 'react-i18next'

import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { useConfirm } from '@/components/ui/confirm-dialog'

interface ProtocolRowProps {
  label: string
  protocol?: { id: string; name?: string }
  generating?: boolean
  uploading?: boolean
  onGenerate: () => void
  onUpload: (file: File) => void
  onView: (id: string, name?: string) => void
  onDelete: (id: string) => void
}

function ProtocolRow({
  label,
  protocol,
  generating,
  uploading,
  onGenerate,
  onUpload,
  onView,
  onDelete
}: ProtocolRowProps) {
  const { t } = useTranslation('flat')
  const inputRef = useRef<HTMLInputElement>(null)

  return (
    <div className="space-y-2.5">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span className="flex items-center gap-2 text-sm font-semibold text-ink">
          <FileCheck className="h-4 w-4 text-primary" />
          {label}
        </span>
        {!protocol && (
          <div className="flex gap-2">
            <Button size="sm" onClick={onGenerate} disabled={generating}>
              <Sparkles className="h-4 w-4" />
              {t('contract.protocol.generate')}
            </Button>
            <input
              type="file"
              accept="application/pdf"
              ref={inputRef}
              className="hidden"
              onChange={(e) => {
                const file = e.target.files?.[0]
                if (file) onUpload(file)
                e.target.value = ''
              }}
            />
            <Button
              size="sm"
              variant="ghostDark"
              onClick={() => inputRef.current?.click()}
              disabled={uploading}
            >
              <Upload className="h-4 w-4" />
              {t('contract.protocol.upload')}
            </Button>
          </div>
        )}
      </div>

      {protocol && (
        <Card variant="light" className="flex items-center justify-between gap-3 p-3">
          <div className="flex min-w-0 items-center gap-3">
            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-ink/[0.08] bg-white text-destructive">
              <FileText className="h-5 w-5" />
            </span>
            <div className="min-w-0">
              <div className="truncate font-medium text-ink">
                {protocol.name}
              </div>
              <span className="mt-0.5 inline-flex items-center rounded-full bg-success-soft px-2 py-0.5 text-xs font-semibold text-success">
                {t('contract.protocol.uploaded')}
              </span>
            </div>
          </div>
          <div className="flex shrink-0 gap-1">
            <Button
              variant="ghostDark"
              size="icon"
              aria-label={t('contract.protocol.view')}
              onClick={() => onView(protocol.id, protocol.name)}
            >
              <Eye className="h-4 w-4" />
            </Button>
            <Button
              variant="ghostDark"
              size="icon"
              aria-label={t('contract.protocol.delete')}
              className="text-destructive hover:text-destructive"
              onClick={() => onDelete(protocol.id)}
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        </Card>
      )}
    </div>
  )
}

export function FlatProtocols({ flatId }: { flatId: string | undefined }) {
  const { t } = useTranslation('flat')
  const { confirm, dialog } = useConfirm()

  const { data: protocols = [] } = useFlatProtocols(flatId)
  const uploadProtocol = useUploadProtocol()
  const deleteProtocol = useDeleteProtocol()
  const generateProtocol = useProtocolPreview()
  const { downloadProtocol } = useDownloadProtocol()

  const handoverProtocol = protocols.find((p) => p.protocol_type === 'HANDOVER')
  const returnProtocol = protocols.find((p) => p.protocol_type === 'RETURN')

  const generate = (protocolType: 'landlord-handover' | 'landlord-return') => {
    if (!flatId) return
    generateProtocol.mutate({ flatId, protocolType })
  }
  const upload = (protocolType: 'HANDOVER' | 'RETURN') => (file: File) => {
    if (!flatId) return
    uploadProtocol.mutate({ flatId, file, protocolType })
  }
  const view = (protocolId: string, protocolName?: string) => {
    if (flatId) downloadProtocol({ flatId, protocolId, protocolName })
  }
  const remove = async (protocolId: string) => {
    if (!flatId) return
    if (
      await confirm({
        title: t('contract.protocol.delete_confirm'),
        destructive: true,
        confirmText: t('contract.yes'),
        cancelText: t('contract.no')
      })
    ) {
      deleteProtocol.mutate({ flatId, protocolId })
    }
  }

  return (
    <Card className="p-5 sm:p-6">
      <h3 className="m-0 mb-8 flex items-center gap-2.5 font-display text-base font-bold tracking-tight text-ink">
        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary">
          <FileCheck className="h-5 w-5" />
        </span>
        {t('contract.protocols')}
      </h3>

      <div className="space-y-4">
        <ProtocolRow
          label={t('contract.protocol.handover_title')}
          protocol={handoverProtocol}
          generating={generateProtocol.isPending}
          uploading={uploadProtocol.isPending}
          onGenerate={() => generate('landlord-handover')}
          onUpload={upload('HANDOVER')}
          onView={view}
          onDelete={remove}
        />
        <div className="h-px w-full bg-ink/[0.06]" />
        <ProtocolRow
          label={t('contract.protocol.return_title')}
          protocol={returnProtocol}
          generating={generateProtocol.isPending}
          uploading={uploadProtocol.isPending}
          onGenerate={() => generate('landlord-return')}
          onUpload={upload('RETURN')}
          onView={view}
          onDelete={remove}
        />
      </div>
      {dialog}
    </Card>
  )
}
